import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Question, ResponsePayload, QuestionFlowItemSummary } from '../types/index';
import { isQuestionResponseValid } from 'utils/questionResponseValidation';
import { normalizeQuestionType } from 'utils/questionTypeUtils';
import { getTestId } from '../utils/testUtils';
import { SubmitButton } from './questions/SubmitButton';
import { CustomScreenQuestion } from './QuestionFlow/screens/CustomScreenQuestion';
import { Image } from './ui/image';
import { Card, CardContent, CardHeader } from './ui/card';
import { LoadingAnimation } from './ui/loading-animation';
import type { SubmitQuestionOptions } from './QuestionFlow/useQuestionFlowManager';
import { CitationList } from './Citations/CitationList';
import {
  normalizeResponseDraft,
  type NormalizedResponseDraft,
} from 'utils/responseNormalization';
import { getQuestionRenderer, type QuestionRendererExtras } from './QuestionFlow/renderers/registry';
import { extractQuestionCitationIndices, QuestionPrompt, QuestionText } from 'utils/questionMarkdown';
import { useIsFlowThemeSurface } from './QuestionFlow/themes/FlowThemeChrome';
import './QuestionFlow/renderers/multipleChoice';
import './QuestionFlow/renderers/multipleSelect';
import './QuestionFlow/renderers/boolean';
import './QuestionFlow/renderers/rating';
import './QuestionFlow/renderers/textList';
import './QuestionFlow/renderers/textOrAudio';
import './QuestionFlow/renderers/dragAndDropRanking';
import './QuestionFlow/renderers/horizontalDragDrop';
import './QuestionFlow/renderers/callbackSummary';
import './QuestionFlow/renderers/accordion';
import './QuestionFlow/renderers/multipleReveal';
import './QuestionFlow/renderers/displayOnly';
import './QuestionFlow/renderers/videoPlayer';
import './QuestionFlow/renderers/pdf';
import './QuestionFlow/renderers/radarChart';
import './QuestionFlow/renderers/unsupported';
import './QuestionFlow/renderers/additionalResources';
import './QuestionFlow/renderers/aiSummary';
import { CompanionAvatar, defaultCompanion } from 'components/Companion';

const AUTO_SYNC_DEBOUNCE_MS = 400;

export interface QuestionCardProps {
  question: Question;
  generationId?: number;
  response?: ResponsePayload;
  onSubmit: (response: ResponsePayload, options?: SubmitQuestionOptions) => void | Promise<void>;
  isSubmitting?: boolean;
  isInteractionLocked?: boolean;
  isQuizFlow?: boolean;
  flowItem?: QuestionFlowItemSummary;
  flowRunId?: number | null;
  isLastQuestion?: boolean;
  lastQuestionButtonText?: string;
  disableAutoSync?: boolean;
  allowClearedOptionalSkip?: boolean;
  hideSubmit?: boolean;
  isSubmitLoading?: boolean;
  afterResponse?: React.ReactNode;
  onDraftChange?: (draft: ResponsePayload | null) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  generationId,
  response,
  onSubmit,
  isSubmitting = false,
  isInteractionLocked,
  isQuizFlow = false,
  flowItem,
  flowRunId,
  isLastQuestion = false,
  lastQuestionButtonText = 'Submit Module',
  disableAutoSync = false,
  allowClearedOptionalSkip = false,
  hideSubmit = false,
  isSubmitLoading = false,
  afterResponse,
  onDraftChange,
}) => {
  const interactionLocked = isInteractionLocked ?? isSubmitting;
  // Themed cards are frosted glass over a moving scene; images keep the opaque
  // white surface they were made for (see the Course Factory media contract).
  const isThemedSurface = useIsFlowThemeSurface();
  const questionId = question?.id ?? null;
  const normalizedQuestionType = useMemo(
    () => normalizeQuestionType(question?.question_type_display ?? question?.question_type ?? ''),
    [question?.question_type_display, question?.question_type],
  );
  const isRequiredQuestion = flowItem?.is_required !== false;
  const existingResponse =
    questionId && response?.question === questionId ? response : undefined;
  const existingAudioUrl = existingResponse?.audio_url ?? null;

  const rendererComponent = useMemo(
    () => getQuestionRenderer(normalizedQuestionType),
    [normalizedQuestionType],
  );
  const usesRenderer = Boolean(rendererComponent);
  const questionCitations = question.citations ?? [];
  const hasVisibleEndpointLabels = ['rating', 'boolean', 'horizontal_drag_drop'].includes(normalizedQuestionType);
  const usedCitationIndices = useMemo(
    () =>
      extractQuestionCitationIndices([
        question.text,
        question.header,
        question.subtext,
        hasVisibleEndpointLabels ? question.low_text : null,
        hasVisibleEndpointLabels ? question.high_text : null,
      ]),
    [hasVisibleEndpointLabels, question.header, question.high_text, question.low_text, question.subtext, question.text],
  );

  const [rendererDraft, setRendererDraft] = useState<ResponsePayload | null>(
    () => existingResponse ?? null,
  );
  const [rendererHasInput, setRendererHasInput] = useState<boolean>(() => Boolean(existingResponse));
  const [rendererIsValid, setRendererIsValid] = useState<boolean>(() => Boolean(existingResponse));
  const [rendererExtras, setRendererExtras] = useState<QuestionRendererExtras>({});

  const normalizePayload = useCallback(
    (payload: ResponsePayload) => normalizeResponseDraft(questionId, payload, normalizedQuestionType),
    [questionId, normalizedQuestionType],
  );

  useEffect(() => {
    setRendererExtras({});
  }, [questionId]);

  const prevQuestionIdRef = useRef<number | null>(questionId);

  useEffect(() => {
    if (!questionId) {
      setRendererDraft(null);
      setRendererHasInput(false);
      setRendererIsValid(false);
      prevQuestionIdRef.current = null;
      return;
    }

    // Only reseed from external state when the question changes.
    // When existingResponse changes for the SAME question (e.g. after
    // a background auto-sync adds a responseId), we must NOT overwrite
    // the user's current rendererDraft — doing so clobbers their input.
    const isNewQuestion = prevQuestionIdRef.current !== questionId;
    prevQuestionIdRef.current = questionId;

    if (!isNewQuestion) {
      return;
    }

    if (existingResponse) {
      setRendererDraft(existingResponse);
      setRendererHasInput(true);
      setRendererIsValid(true);
    } else {
      setRendererDraft(null);
    }
  }, [existingResponse, questionId]);

  const handleRendererDraftChange = useCallback((draft: ResponsePayload | null) => {
    setRendererDraft(draft);
    onDraftChange?.(draft);
  }, [onDraftChange]);

  const handleRendererHasInputChange = useCallback((hasInput: boolean) => {
    setRendererHasInput(hasInput);
  }, []);

  const handleRendererValidityChange = useCallback((isValid: boolean) => {
    setRendererIsValid(isValid);
  }, []);

  const handleRendererExtrasChange = useCallback((extras: QuestionRendererExtras) => {
    setRendererExtras(extras);
  }, []);

  const currentPayload = useMemo<ResponsePayload>(() => {
    if (!questionId) {
      return { question: -1 };
    }

    if (usesRenderer) {
      if (!rendererDraft) {
        return { question: questionId };
      }

      const resolvedQuestionId =
        typeof rendererDraft.question === 'number' ? rendererDraft.question : questionId;

      return {
        ...rendererDraft,
        question: resolvedQuestionId,
      };
    }

    return { question: questionId };
  }, [questionId, rendererDraft, usesRenderer]);

  const normalizedPayload = useMemo<NormalizedResponseDraft>(
    () => normalizePayload(currentPayload),
    [currentPayload, normalizePayload],
  );

  const validationPayload = currentPayload;

  const normalizedValidationPayload = useMemo<NormalizedResponseDraft>(
    () => normalizePayload(validationPayload),
    [normalizePayload, validationPayload],
  );

  const currentSignature = useMemo(
    () => JSON.stringify(normalizedPayload),
    [normalizedPayload],
  );

  const lastSubmittedSignatureRef = useRef<string | null>(null);
  const autoSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestOnSubmitRef = useRef<typeof onSubmit>(onSubmit);

  useEffect(() => {
    latestOnSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    const basePayload: ResponsePayload =
      existingResponse ?? { question: typeof questionId === 'number' ? questionId : -1 };
    const normalizedExisting = normalizePayload(basePayload);
    lastSubmittedSignatureRef.current = JSON.stringify(normalizedExisting);
  }, [existingResponse, normalizePayload, questionId]);

  const {
    audioBlob: extraAudioBlob = null,
    horizontalState,
    isVideoValid,
    isPdfValid,
    isAiSummaryLoading = false,
  } = rendererExtras;

  const requiresRendererValidity =
    usesRenderer && normalizedQuestionType !== 'image' && normalizedQuestionType !== 'description' && normalizedQuestionType !== 'additional_resources' && normalizedQuestionType !== 'ai_summary';

  const responseIsValid = useMemo(() => {
    if (!question) {
      return false;
    }

    const computed = isQuestionResponseValid(question, {
      draft: validationPayload,
      selectedOptions: normalizedValidationPayload.selected_options,
      customOtherValue: normalizedValidationPayload.custom_other_value,
      textValue: normalizedValidationPayload.text_value,
      booleanValue: normalizedValidationPayload.boolean_value,
      ratingValue: normalizedValidationPayload.rating_value,
      textListValue: normalizedPayload.text_list,
      horizontalDragState: horizontalState,
      audioBlob: extraAudioBlob,
      audioUrl: existingAudioUrl,
      isVideoResponseValid: isVideoValid,
      isPdfValid: isPdfValid,
    });

    return requiresRendererValidity ? computed && rendererIsValid : computed;
  }, [
    question,
    validationPayload,
    normalizedPayload,
    normalizedValidationPayload,
    horizontalState,
    extraAudioBlob,
    existingAudioUrl,
    isVideoValid,
    isPdfValid,
    requiresRendererValidity,
    rendererIsValid,
  ]);

  const hasUserResponse = usesRenderer
    ? rendererHasInput || Boolean(existingResponse)
    : Boolean(existingResponse);

  const supportsAutoSync = useMemo(() => {
    const autoSyncTypes = [
      'rating',
      'multiple_choice',
      'multiple_select',
      'drag_and_drop',
      'drag_and_drop_ranking',
      'horizontal_drag_drop',
      'boolean',
      'text_or_audio',
      'text_list',
      'multiple_reveal',
      'callback_summary',
    ];
    return autoSyncTypes.includes(normalizedQuestionType);
  }, [normalizedQuestionType]);

  const clearAutoSyncTimeout = useCallback(() => {
    if (autoSyncTimeoutRef.current) {
      clearTimeout(autoSyncTimeoutRef.current);
      autoSyncTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!supportsAutoSync || !questionId || disableAutoSync) {
      clearAutoSyncTimeout();
      return;
    }

    if (!hasUserResponse) {
      clearAutoSyncTimeout();
      return;
    }

    const previousSignature = lastSubmittedSignatureRef.current;
    if (previousSignature === currentSignature) {
      return;
    }

    clearAutoSyncTimeout();

    autoSyncTimeoutRef.current = setTimeout(() => {
      lastSubmittedSignatureRef.current = currentSignature;
      const submit = latestOnSubmitRef.current;
      if (submit) {
        void submit(currentPayload, { advance: false });
      }
    }, AUTO_SYNC_DEBOUNCE_MS);

    return clearAutoSyncTimeout;
  }, [
    clearAutoSyncTimeout,
    currentPayload,
    currentSignature,
    questionId,
    hasUserResponse,
    supportsAutoSync,
    disableAutoSync,
  ]);

  const handleSubmit = useCallback(
    (override?: ResponsePayload, options?: SubmitQuestionOptions) => {
      if (!questionId) {
        return;
      }

      const payloadToSubmit = override
        ? {
            ...currentPayload,
            ...override,
            question: typeof override.question === 'number' ? override.question : questionId,
          }
        : currentPayload;

      const normalizedPayloadSignature = JSON.stringify(normalizePayload(payloadToSubmit));
      const shouldAdvance = options?.advance ?? true;

      if (!shouldAdvance && lastSubmittedSignatureRef.current === normalizedPayloadSignature) {
        return;
      }

      clearAutoSyncTimeout();
      lastSubmittedSignatureRef.current = normalizedPayloadSignature;

      return onSubmit(payloadToSubmit, { ...options, advance: shouldAdvance });
    },
    [clearAutoSyncTimeout, currentPayload, normalizePayload, onSubmit, questionId],
  );

  if (!question) {
    return (
      <div className="flex min-h-[25rem] w-full items-center justify-center">
        <LoadingAnimation size="lg" />
      </div>
    );
  }

  if (normalizedQuestionType === 'custom_screen') {
    return (
      <div className="flex justify-center">
        <div className="flex w-full max-w-5xl justify-center" {...getTestId(`question-card-${question.id}`)}>
          <CustomScreenQuestion
            question={question}
            response={existingResponse}
            isSubmitting={isSubmitting}
            onSubmit={(payload, options) => handleSubmit(payload, options)}
          />
        </div>
      </div>
    );
  }

  const renderQuestionContent = () => {
    if (!usesRenderer || !rendererComponent) {
      console.warn(`[QuestionCard] Unknown question type: ${normalizedQuestionType}`);
      return null;
    }

    const Renderer = rendererComponent;
    return (
      <Renderer
        question={question}
        existingResponse={existingResponse}
        isSubmitting={interactionLocked}
        isQuizFlow={isQuizFlow}
        flowRunId={flowRunId}
        onDraftChange={handleRendererDraftChange}
        onHasUserInputChange={handleRendererHasInputChange}
        onValidityChange={handleRendererValidityChange}
        onExtrasChange={handleRendererExtrasChange}
      />
    );
  };

  const isSubmitDisabled = () => {
    if (interactionLocked) {
      return true;
    }
    if (normalizedQuestionType === 'ai_summary' && isAiSummaryLoading) {
      return true;
    }

    // multiple_reveal must stay gated until all cards are flipped at least once,
    // even when the flow item is optional.
    if (!isRequiredQuestion && !(allowClearedOptionalSkip && usesRenderer ? rendererHasInput : hasUserResponse) && normalizedQuestionType !== 'multiple_reveal') {
      return false;
    }

    return !responseIsValid;
  };

  const renderSubmitButton = () => {
    if (normalizedQuestionType === 'custom_screen' || hideSubmit) {
      return null;
    }

    let buttonText = 'Continue';

    if (isLastQuestion) {
      buttonText = lastQuestionButtonText;
    }

    const actualIsDisabled = isSubmitDisabled();
    // Keep button disabled during in-flight syncs, but avoid a spinner on normal
    // per-question "Continue" clicks to reduce visual churn.
    const actualIsLoading = isSubmitLoading || (isSubmitting && buttonText !== 'Continue');

    return (
      <SubmitButton
        onSubmit={() => handleSubmit(undefined, { advance: true })}
        isDisabled={actualIsDisabled}
        isLoading={actualIsLoading}
        buttonText={buttonText}
        isAlreadySubmitted={false}
      />
    );
  };

  const resolveImageUrl = (targetQuestion: Question) => {
    if (targetQuestion.media?.media_type === 'image') {
      if (targetQuestion.media?.url) {
        return targetQuestion.media.url;
      }
      if (targetQuestion.media?.storage_key) {
        return targetQuestion.media.storage_key;
      }
    }
    if (targetQuestion.visual_component_url) {
      return targetQuestion.visual_component_url;
    }
    if (targetQuestion.image_url) {
      return targetQuestion.image_url;
    }
    return null;
  };

  const isValidImageQuestion = (targetQuestion: Question) => {
    const imageUrl = resolveImageUrl(targetQuestion);
    if (!imageUrl) {
      return false;
    }

    const questionType = targetQuestion.question_type_display;
    switch (questionType) {
      case 'image':
        return true;
      default:
        return false;
    }
  };

  const getMaxWidthForQuestionType = (targetQuestion: Question) => {
    const questionType = targetQuestion.question_type_display;
    switch (questionType) {
      case 'horizontal_drag_drop':
        return 'w-full max-w-[calc(100vw-3.25rem)] sm:max-w-[62.5rem]';
      case 'video_player':
        return 'w-full max-w-[calc(100vw-3.25rem)] sm:max-w-[62.5rem]';
      default:
        return 'w-full max-w-[calc(100vw-3.25rem)] sm:max-w-[41.25rem]';
    }
  };

  const getMinWidthForQuestionType = (questionType: string) => {
    switch (questionType) {
      case 'text_or_audio':
        return 'sm:min-w-[34rem]';
      case 'callback_summary':
        return 'sm:min-w-[34rem]';
      default:
        return '';
    }
  };

  const renderedTitle = <QuestionPrompt text={question.text} citations={questionCitations} />;
  const renderedSubtext = question.subtext?.trim()
    ? <QuestionText text={question.subtext} citations={questionCitations} answerReferences />
    : null;

  const submitButton = renderSubmitButton();
  // Personalized wording is marked by the star alone, beside the button; its name stays available to screen readers.
  const personalizedMark = generationId != null && !isQuizFlow ? (
    <div className="flex shrink-0 items-center" data-testid="personalized-question-mark">
      <CompanionAvatar size={24} label={defaultCompanion.displayName} />
    </div>
  ) : null;
  const questionContent = renderQuestionContent();
  const cardWidthClasses = getMaxWidthForQuestionType(question);
  const cardMinWidthClasses = getMinWidthForQuestionType(normalizedQuestionType);
  const shouldShowCitations = questionCitations.length > 0;
  const mobileQuizCardClasses = isQuizFlow
    ? 'flex min-h-[calc(100dvh-11.5rem)] flex-col sm:min-h-0'
    : '';

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex justify-center w-full">
        <div className="flex items-center">
          <Card
            maxWidth="none"
            disableAnimation
            {...getTestId(`question-card-${question.id}`)}
            padding="none"
            variant="glass"
            data-flow-surface="card"
            className={`${cardWidthClasses} ${cardMinWidthClasses} ${mobileQuizCardClasses} w-full min-w-0 pt-5`}
          >
            {isValidImageQuestion(question) && (
              <div className="flex animate-fade-in justify-center pt-3 pb-5">
                <Image
                  imageUrl={resolveImageUrl(question)!}
                  alt={question.media?.alt_text ?? 'Question asset'}
                  className={isThemedSurface ? 'bg-white' : undefined}
                  data-flow-image-backing={isThemedSurface ? 'white' : undefined}
                  data-testid="question-image-frame"
                />
              </div>
            )}
            {question.header != null && (
              <CardHeader className="px-5 pb-2 uppercase sm:px-10">
                <QuestionText text={question.header} citations={questionCitations} inline answerReferences />
              </CardHeader>
            )}

            <div className="px-5 pb-4 sm:px-10">
              {renderedTitle}
              {renderedSubtext && (
                <div className="self-stretch pt-4 text-foreground font-body-m font-[var(--body-m-font-weight)] text-[length:var(--body-m-font-size)] leading-relaxed">
                  {renderedSubtext}
                </div>
              )}
            </div>

            {normalizedQuestionType !== 'image' &&
              normalizedQuestionType !== 'description' && (
                <CardContent variant="userResponse" padding="large">
                  {questionContent}
                </CardContent>
              )}

            {normalizedQuestionType === 'image' || normalizedQuestionType === 'description'
              ? questionContent
              : null}

            {afterResponse ? <div className="px-5 pb-4 sm:px-10">{afterResponse}</div> : null}

            {submitButton || personalizedMark ? (
              <div data-testid="question-actions" className={`flex w-full items-center justify-between gap-3 px-5 pb-7 pt-4 sm:px-10 ${isQuizFlow ? 'mt-auto' : ''}`}>
                <div className="min-w-0">{submitButton}</div>
                {personalizedMark}
              </div>
            ) : null}
          </Card>
        </div>
      </div>
      {shouldShowCitations ? (
        <div className="flex w-full justify-center">
          <div className={`${cardWidthClasses} ${cardMinWidthClasses} w-full`}>
            <CitationList citations={questionCitations} usedIndices={usedCitationIndices} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QuestionCard;
